import { useMemo, type JSX } from "react";
import type { components } from "../../api/schema";
import { useWorldlineScenes } from "../../api/hooks/useWorldlineScenes";
import { useForkWorldline } from "../../api/hooks/useForkWorldline";
import { useHistory } from "../../api/hooks/useHistory";
import { mapProblemDetail } from "../../errors/mapProblemDetail";
import { ErrorBanner } from "../player/ErrorBanner";
import { SceneView } from "../player/SceneView";
import { BeatTimelineView } from "../../components/beat/BeatTimelineView";

type Character = components["schemas"]["Character"];

export type BackscrollViewProps = {
  worldlineId: string;
  scenarioId: string;
  /** 現在表示中のシーン ID。一致するシーンは選択 UI 付きで描画される。null の場合は全シーンを readOnly で描画する。 */
  currentSceneId: string | null;
  characters: Character[];
  /** fork 成功時に新しい世界線 ID を渡すコールバック */
  onForkSuccess?: (newWorldlineId: string) => void;
  /**
   * 履歴 hint を取得する playline ID。
   * 未指定の場合は履歴 fetch を行わず、全 fork-button が hint なしで描画される (既存挙動互換)。
   */
  playlineId?: string | undefined;
};

/**
 * バックスクロールビュー。
 * 指定された世界線のシーン一覧を上から順に表示し、各過去シーンに「ここから分岐」ボタンを配置する。
 * currentSceneId に一致するシーンのみ選択 UI 付きで表示し、それ以外は readOnly で描画する。
 */
export function BackscrollView({
  worldlineId,
  scenarioId,
  currentSceneId,
  characters,
  onForkSuccess,
  playlineId,
}: BackscrollViewProps): JSX.Element {
  const { data: scenes, isError, error, refetch } = useWorldlineScenes(worldlineId);
  const forkMutation = useForkWorldline(scenarioId);
  // 履歴 hint 用。playlineId 未指定または fetch 未完了/失敗時は data === undefined となり、
  // historyMap が空のまま全 fork-button が hint なしで描画される (graceful degradation)。
  const { data: historyData } = useHistory(playlineId);
  const historyMap = new Map<string, string>(
    historyData?.map((entry) => [entry.sceneId, entry.choiceId]) ?? [],
  );

  // scene-beat-decomposition production 配線 第 3 件:
  // backscroll timeline 上部に scenario の Beat timeline を読み取り専用で表示。 reader が過去
  // scene を遡って見るときの narrative 構造把握 context を提供する。 baseUrl は既存
  // ScenarioBuilderApp pattern と同じく VITE_API_BASE_URL から resolve、 Beat 未設定 scenario
  // (= 既存 50+) では BeatTimelineView 内部で empty state を表示し reader UX を破壊しない。
  const beatApiBaseUrl = useMemo<string>(
    () => (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "",
    [],
  );

  if (isError) {
    const uiError = mapProblemDetail(error);
    return (
      <div data-testid="backscroll-view-error" className="p-4">
        <ErrorBanner
          error={uiError}
          onRetry={() => { void refetch(); }}
        />
      </div>
    );
  }

  if (scenes === undefined) {
    // クエリがまだ完了していない（ローディング中）
    return (
      <div data-testid="backscroll-view-loading" className="p-4 text-reader-muted">
        シーンを読み込み中…
      </div>
    );
  }

  const handleFork = (sceneId: string, choiceId: string): void => {
    forkMutation.mutate(
      { worldlineId, branchFromSceneId: sceneId, branchFromChoiceId: choiceId },
      {
        onSuccess: (newWorldline) => {
          onForkSuccess?.(newWorldline.id);
        },
      },
    );
  };

  return (
    <div data-testid="backscroll-view" className="flex flex-col gap-8">
      <section
        data-testid="backscroll-beat-timeline-section"
        className="flex flex-col gap-2 border-b border-reader-border pb-4"
      >
        <h3 className="text-sm font-semibold text-reader-secondary">
          物語構造 (beat timeline)
        </h3>
        <BeatTimelineView baseUrl={beatApiBaseUrl} scenarioId={scenarioId} />
      </section>
      {scenes.map((scene) => {
        const isCurrent = scene.id === currentSceneId;

        return (
          <div key={scene.id} data-testid={`backscroll-scene-${scene.id}`} className="flex flex-col gap-2">
            <SceneView
              scene={scene}
              isPending={false}
              onSelectChoice={() => {}}
              onAdvance={() => {}}
              characters={characters}
              readOnly={!isCurrent}
            />
            {/* 過去シーン（非カレント）に「ここから分岐」ボタンを配置する。
                選択肢がある場合は各選択肢ごとに分岐可能にする。
                選択肢がない場合は分岐操作を提供しない（エンディング・続行のみのシーン）。 */}
            {!isCurrent && scene.choices.length > 0 && (
              <div className="flex flex-wrap gap-2 pl-2 border-l-2 border-reader-border-strong">
                {scene.choices.map((choice) => {
                  const chosenChoiceId = historyMap.get(scene.id);
                  const hasHistoryContext = chosenChoiceId !== undefined;
                  const isChosen = chosenChoiceId === choice.id;

                  // 基底 className は既存と完全一致させる (history 未解決時の bit-by-bit 互換性確保)
                  const baseClassName =
                    "min-h-11 px-3 py-1 text-sm bg-indigo-900/60 hover:bg-indigo-800 text-indigo-200 rounded border border-indigo-700/50 disabled:opacity-50 disabled:cursor-not-allowed";

                  // hasHistoryContext === false (history 未解決 / loading / error) は基底のみ
                  let className = baseClassName;
                  let dataChosen: "true" | "false" | undefined;

                  if (hasHistoryContext) {
                    dataChosen = isChosen ? "true" : "false";
                    className = isChosen
                      ? `${baseClassName} border-l-4 border-amber-400 bg-indigo-800/80 ring-1 ring-amber-400/30`
                      : `${baseClassName} opacity-60`;
                  }

                  return (
                    <button
                      key={choice.id}
                      type="button"
                      data-testid={`fork-button-${scene.id}-${choice.id}`}
                      data-chosen={dataChosen}
                      disabled={forkMutation.isPending}
                      onClick={() => handleFork(scene.id, choice.id)}
                      className={className}
                    >
                      「{choice.text}」からここで分岐
                      {hasHistoryContext && isChosen && (
                        // SR は既存 button text を読むため marker は視覚のみ (aria-hidden)
                        <span aria-hidden="true" className="ml-2 text-amber-300">
                          ✓ 選択済
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
